import { FlashbarProps } from '@amzn/awsui-components-core';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchAllApplications,
  fetchAnnouncements,
  fetchApplicationDropdowns,
  fetchFavoriteApplications,
  fetchHomePageContent,
  fetchTeamPageContent
} from 'src/api/app-sync-services';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { eLocalStorageKeys, MODULE_DISPLAY_ORDER } from 'src/constants/AppConstants';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { logger } from 'src/logger';
import { AgGridLicenseService } from 'src/services';
import { HomepageEntity } from '../das-finsuite/admin-components/homepage-manage/HomepageModel';
import { FinTechTeams } from '../das-finsuite/admin-components/teams-manage/TeamManagementModel';
import { LoadingSpinner } from '../generic-components/LoadingSpinner';
import { AppContext, LoadingStatus, TestimonialEntity } from './AppContextModels';
import { useAuth } from './AuthContextProvider';
import { listTestimonials } from 'src/graphql/queries';

const AppContextInitialData: AppContext = {
  isAdminControlsEnabled: null,
  setIsAdminControlsEnabled: () => {},
  contextLoadingError: null,
  contextLoadingStatus: LoadingStatus.NotInitiated,
  listOfApplications: [],
  setListOfApplications: () => {},
  parsedApplications: [],
  setParsedApplications: () => {},
  favoriteApplications: [],
  setFavoriteApplications: () => {},
  announcements: [],
  setAnnouncements: () => {},
  announcementsFlatEntity: [],
  setAnnouncementsFlatEntity: () => {},
  applicationMessages: [],
  displayApplicationFlashMessage: () => {},
  listApplicationDropdowns: undefined,
  setListApplicationDropdowns: () => {},
  homePageContent: {} as HomepageEntity,
  setHomePageContent: () => {},
  teamPageContent: {} as FinTechTeams,
  setTeamPageContent: () => {},
  testimonials: [],
  distinctModules: []
};

const AppContextDetails = createContext<AppContext>(AppContextInitialData);
export const useAppContext = () => useContext(AppContextDetails);

interface AppContextProviderProps {
  children: React.ReactNode;
}

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const userAuthData = useAuth();
  const [appContext, setAppContext] = useState<AppContext>(AppContextInitialData);
  const [isAdminControlsEnabled, setIsAdminControlsEnabled] = usePersistedState(eLocalStorageKeys.IsAdminControlsEnabled, true);

  useEffect(() => {
    // Allow app context to initialize even on Auth Fail to prevent deadlock if Appsync config fails after retries
    if (userAuthData.userAuthDataLoadingStatus === LoadingStatus.Completed || userAuthData.userAuthDataLoadingStatus === LoadingStatus.Failed) {
      loadAppContext();
    }
  }, [userAuthData]);

  const loadAppContext = async () => {
    logger.info('User authenticated successfully!');
    AgGridLicenseService.configureLicense();
    setAppContext((prev) => ({ ...prev, contextLoadingStatus: LoadingStatus.Loading }));

    try {
      const [homeContent, teamContent] = await Promise.all([fetchHomePageContent(userAuthData.Alias), fetchTeamPageContent(userAuthData.Alias)]);

      const [applications, dropdowns, favorites] = await Promise.all([
        fetchAllApplications(teamContent),
        fetchApplicationDropdowns(teamContent),
        fetchFavoriteApplications(userAuthData.Alias)
      ]);

      const announcements = await fetchAnnouncements(applications.allApplications);

      setAppContext((prev) => ({
        ...prev,
        listOfApplications: applications.allApplications,
        parsedApplications: applications.applicationFlatTableEntity,
        listApplicationDropdowns: dropdowns,
        favoriteApplications: favorites,
        announcements: announcements.announcementList,
        announcementsFlatEntity: announcements.announcementsFlatTableEntity,
        homePageContent: homeContent,
        teamPageContent: teamContent,
        contextLoadingStatus: LoadingStatus.Completed
      }));

      // Fire-and-forget: fetch testimonials after main context is ready
      fetchGraphQLRequest<{ listTestimonials: TestimonialEntity[] }>({
        query: listTestimonials,
        variables: {},
        requestName: 'listTestimonials',
        requestType: 'query'
      })
        .then((response) => {
          const items = (response?.listTestimonials || []).filter((t) => t.is_active !== false);
          setAppContext((prev) => ({ ...prev, testimonials: items }));
        })
        .catch((err) => logger.warn('Failed to fetch testimonials', { error: err }));
    } catch (error) {
      handleError('Error while loading context. Some of the features in Application might not work as expected.');
    }
  };

  const handleError = (errorMessage: string) => {
    setAppContext((prev) => ({
      ...prev,
      listOfApplications: [],
      parsedApplications: [],
      contextLoadingError: errorMessage,
      contextLoadingStatus: LoadingStatus.Failed
    }));
    logger.error(errorMessage);
  };

  const displayApplicationFlashMessage = (content: string, flashBarType: FlashbarProps.Type) => {
    const newFlashMessage: FlashbarProps.MessageDefinition = {
      type: flashBarType,
      content: content,
      dismissible: true,
      dismissLabel: 'Dismiss message',
      onDismiss: () => setAppContext((prev) => ({ ...prev, applicationMessages: [] }))
    };
    addFlashbarItem(newFlashMessage);
  };

  const addFlashbarItem = (item: FlashbarProps.MessageDefinition) => {
    setAppContext((prev) => {
      const newMessages = prev.applicationMessages.length >= 1 ? [...prev.applicationMessages.slice(1), item] : [...prev.applicationMessages, item];
      return { ...prev, applicationMessages: newMessages };
    });
  };

  const distinctModules = useMemo(() => {
    const moduleSet = new Set<string>();
    let hasOther = false;
    appContext.parsedApplications
      .filter((app) => app.isActive)
      .forEach((app) => {
        if (app.glAccount && app.glAccount !== 'N/A') {
          moduleSet.add(app.glAccount);
        } else {
          hasOther = true;
        }
      });
    const knownOrder = MODULE_DISPLAY_ORDER.filter((m) => m !== 'Other');
    const ordered = knownOrder.filter((m) => moduleSet.has(m));
    const unlisted = Array.from(moduleSet)
      .filter((m) => !knownOrder.includes(m))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return [...ordered, ...unlisted, ...(hasOther ? ['Other'] : [])];
  }, [appContext.parsedApplications]);

  if (userAuthData.userAuthDataLoadingStatus === LoadingStatus.Loading || appContext.contextLoadingStatus === LoadingStatus.Loading) {
    return <LoadingSpinner />;
  }

  const contextValue: AppContext = {
    ...appContext,
    isAdminControlsEnabled,
    setIsAdminControlsEnabled,
    displayApplicationFlashMessage,
    distinctModules,
    setListOfApplications: (applications) => setAppContext((prev) => ({ ...prev, listOfApplications: applications })),
    setParsedApplications: (parsedApplications) => setAppContext((prev) => ({ ...prev, parsedApplications })),
    setFavoriteApplications: (favoriteApplications) => setAppContext((prev) => ({ ...prev, favoriteApplications })),
    setAnnouncements: (announcements) => setAppContext((prev) => ({ ...prev, announcements })),
    setAnnouncementsFlatEntity: (announcements) => setAppContext((prev) => ({ ...prev, announcementsFlatEntity: announcements })),
    setListApplicationDropdowns: (applicationDropdowns) => setAppContext((prev) => ({ ...prev, listApplicationDropdowns: applicationDropdowns })),
    setHomePageContent: (homePageContent) => setAppContext((prev) => ({ ...prev, homePageContent })),
    setTeamPageContent: (teamPageContent) => setAppContext((prev) => ({ ...prev, teamPageContent }))
  };

  return <AppContextDetails.Provider value={contextValue}>{children}</AppContextDetails.Provider>;
};
